// src/store/services/variantService.js
// This file contains the corrected and simplified service functions.
import { supabase } from '../../supabaseClient';

export const getVariantsByProductId = async (productId) => {
  const { data, error } = await supabase
    .from('variants')
    .select('*')
    .eq('product_id', productId);
  if (error) throw error;
  return data;
};

export const getVariantsByShopId = async (shopId) => {
  const { data, error } = await supabase
    .from('variants')
    .select(`
      *,
      products (
        card_images
      )
    `)
    .eq('shop_id', shopId);

  if (error) throw error;

  // Format the data so each variant carries the product's center image URL
  return data.map(variant => {
    let productImg = '';
    
    if (variant.products?.card_images) {
      // Safely parse card_images if it's stored as a stringified JSON
      const images = typeof variant.products.card_images === 'string'
        ? JSON.parse(variant.products.card_images)
        : variant.products.card_images;
        
      productImg = images?.center || '';
    }

    return {
      ...variant,
      image_url: productImg // Replaces the empty string with the product's card image
    };
  });
};
export const createVariant = async (variantData) => {
  const { data, error } = await supabase
    .from('variants')
    .insert([variantData])
    .select();
  if (error) throw error;
  return data;
};

export const updateVariant = async (id, updatedVariantData) => {
  // Correctly use the 'id' field to match the primary key in the database
  const { data, error } = await supabase
    .from('variants')
    .update(updatedVariantData)
    .eq('id', id)
    .select();
  if (error) throw error;
  return data;
};

export const deleteVariant = async (id) => {
  const { error } = await supabase
    .from('variants')
    .delete()
    .eq('id', id);

  if (error) {
    throw error;
  }
};

// New function to get variants with stock below a threshold
export const fetchLowStockVariants = async (threshold) => {
  const { data, error } = await supabase
    .from('variants')
    .select('*')
    .lt('in_stock', threshold); // 'lt' stands for 'less than'
  if (error) throw error;
  return data;
};

// src/store/services/variantService.js

// ... (existing imports and functions) ...

/**
 * Fetches a limited number of variants for a specific category, with offset for pagination.
 * @param {string} category - The category to filter by (e.g., 'Laptops').
 * @param {number} limit - The maximum number of variants to return (page size).
 * @param {number} offset - The starting index for the query (for pagination).
 * @returns {Promise<Array>} - A promise that resolves to an array of variant objects.
 */
export const fetchVariantsByCategory = async (category, limit, offset) => {
  if (!category) {
    throw new Error("Category must be provided for fetching variants.");
  }
  
  // Supabase uses range() for offset/limit, where `to` is exclusive.
  const { data, error } = await supabase
    .from('variants')
    .select('*')
    .eq('category', category)
    .range(offset, offset + limit - 1) // limit is size, offset is start index
    .limit(limit); // Redundant with range, but good practice
    
  if (error) {
    console.error("Supabase Error fetching variants by category:", error);
    throw error;
  }
  return data;
};

// Dummy functions for reservation and stock update, to be implemented
export const reserveItem = async ({ variantId, quantity }) => {
  console.log(`Reserving ${quantity} items for variant ${variantId}`);
  return { success: true };
};

export const cancelReservation = async ({ variantId, quantity }) => {
  console.log(`Canceling ${quantity} items for variant ${variantId}`);
  return { success: true };
};

export const updateStock = async (id, stockUpdate) => {
  console.log(`Updating stock for variant ${id} with:`, stockUpdate);
  return { success: true };
};

export const confirmReservation = async ({ variantId, quantity }) => {
  console.log(`Confirming reservation for variant ${variantId}`);
  return { success: true };
};



// --------------------------------------------------------

// ---------------------------------------------------------
// VARIANTS_CATEGORIES SUPPORT
// For product types OUTSIDE the static Electronics category list
// (clothes, shoes, artisan collections, etc.) — these variants store
// their category as free text in variants.category, matched against a
// real row in variants_categories instead of the app's fixed
// PRODUCT_CATEGORIES list. Electronics variants and everything above this
// point are untouched by this — they keep using the static category list.
// ---------------------------------------------------------

export const fetchVariantsCategoriesByShopId = async (shopId) => {
  const { data, error } = await supabase
    .from('variants_categories')
    .select('*')
    .eq('shop_id', shopId)
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return data;
};

// Combined read for a future "collections" style menu dashboard — reuses
// getVariantsByShopId above (already joins product card_images) so the
// item shape stays identical to what the rest of the app expects.
export const fetchEntireShopVariantCategoryMenuData = async (shopId) => {
  const [categoriesResponse, variants] = await Promise.all([
    supabase
      .from('variants_categories')
      .select('*')
      .eq('shop_id', shopId)
      .order('sort_order', { ascending: true }),
    getVariantsByShopId(shopId),
  ]);

  if (categoriesResponse.error) throw categoriesResponse.error;

  return {
    categories: categoriesResponse.data || [],
    variants: variants || [],
  };
};

export const createVariantCategory = async (shopId, name, imageUrl, sortOrder) => {
  const { data, error } = await supabase
    .from('variants_categories')
    .insert([{
      shop_id: shopId,
      name,
      img_url: imageUrl,
      sort_order: sortOrder,
      is_active: true,
    }])
    .select()
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error('Category creation failed. Check Supabase RLS insert policies.');
  return data;
};

// UPDATED: renaming a variants_categories row cascades the new name onto
// every variants row that had the old category name — this is the fix
// that keeps a typo/rename in a collection name from breaking every item
// in it.
export const updateVariantCategoryWithCascade = async (shopId, categoryId, oldName, newName, imageUrl = null) => {
  const updates = { name: newName, updated_at: new Date().toISOString() };
  if (imageUrl) updates.img_url = imageUrl;

  const { data: updatedCategory, error: categoryError } = await supabase
    .from('variants_categories')
    .update(updates)
    .eq('id', categoryId)
    .select()
    .maybeSingle();

  if (categoryError) throw categoryError;
  if (!updatedCategory) throw new Error('Failed to update category. Check RLS policies on variants_categories.');

  if (oldName !== newName) {
    const { error: cascadeError } = await supabase
      .from('variants')
      .update({ category: newName, updated_at: new Date().toISOString() })
      .eq('shop_id', shopId)
      .eq('category', oldName);

    if (cascadeError) throw cascadeError;
  }

  return updatedCategory;
};

// Soft delete: flips is_active on the category row, and detaches any
// variants still pointing at the disabled category name.
export const softDeleteVariantCategoryWithCascade = async (shopId, categoryId, categoryNameToDisable) => {
  const { data: updatedCategory, error: categoryError } = await supabase
    .from('variants_categories')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('id', categoryId)
    .select()
    .single();

  if (categoryError) throw categoryError;

  const { error: itemUpdateError } = await supabase
    .from('variants')
    .update({ category: null, updated_at: new Date().toISOString() })
    .eq('shop_id', shopId)
    .eq('category', categoryNameToDisable);

  if (itemUpdateError) throw itemUpdateError;
  return updatedCategory;
};

export const saveVariantCategoriesListOrder = async (orderedCategories) => {
  const promises = orderedCategories.map(cat =>
    supabase
      .from('variants_categories')
      .update({ sort_order: cat.sort_order, updated_at: new Date().toISOString() })
      .eq('id', cat.id)
  );

  await Promise.all(promises);
  return orderedCategories;
};

// Append to src/store/services/variantService.js

export const getVariantById = async (id) => {
    if (!id) return null;
    const { data, error } = await supabase
        .from('variants')
        .select('*')
        .eq('id', id)
        .maybeSingle();
    if (error) throw error;
    return data;
};

// Powers the "similar products" row on ProductPageGeneric — same category,
// excluding the item currently being viewed.
export const getSimilarVariants = async ({ category, excludeId, limit = 10 }) => {
    if (!category) return [];
    const { data, error } = await supabase
        .from('variants')
        .select('*')
        .eq('category', category)
        .neq('id', excludeId)
        .eq('is_hidden', false)
        .order('created_at', { ascending: false })
        .limit(limit);
    if (error) throw error;
    return data || [];
};

///////////////////////////////////////////////////////
// Append to src/store/services/variantService.js

const normalizeForMatch = (text) =>
    text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

// Primary: match variants whose search_tags_vector contains every
// significant word of the current product's name (same multi-word AND
// pattern already used by searchVariantsByQuery, so results behave the
// same way normal search does — not a strict 100% name match).
// Fallback: if nothing matches by name, fall back to same-category items,
// so the section is never empty just because the name is unique.
export const getSimilarVariantsByName = async ({ productName, category, excludeId, limit = 10 }) => {
    if (!productName) return [];
    const words = normalizeForMatch(productName).split(/\s+/).filter((w) => w.length > 1);

    try {
        let query = supabase
            .from('variants')
            .select('*')
            .neq('id', excludeId)
            .eq('is_hidden', false)
            .order('created_at', { ascending: false })
            .limit(limit);

        words.forEach((word) => {
            query = query.ilike('search_tags_vector', `%${word}%`);
        });

        const { data, error } = await query;
        if (error) throw error;
        if (data && data.length > 0) return data;
    } catch (e) {
        console.error('[variantService] name-similarity search failed:', e);
    }

    if (!category) return [];
    const { data, error } = await supabase
        .from('variants')
        .select('*')
        .eq('category', category)
        .neq('id', excludeId)
        .eq('is_hidden', false)
        .order('created_at', { ascending: false })
        .limit(limit);
    if (error) return [];
    return data || [];
};

// ---------------------------------------------------------
// HOME PAGE: random non-electronics products, distributed across shops
// ---------------------------------------------------------

const shuffleArray = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

/**
 * Fetches up to `limit` random product variants for the home page,
 * excluding anything with product_type "Electronics" (that already has
 * its own dedicated home section). Distribution rule: if 10+ distinct
 * shops have eligible products, show exactly 1 item per shop (10 shops,
 * 10 items); if fewer than 10 shops exist, round-robin across the shops
 * that do exist so every shop contributes roughly evenly (1 or 2 items
 * each) until `limit` is reached.
 *
 * Randomness note: Supabase/PostgREST has no built-in "random row" query,
 * so this pulls a generous pool (POOL_SIZE) of eligible variants and
 * shuffles client-side before distributing — cheap and good enough at
 * this data scale, and avoids needing a Postgres function.
 */
export const fetchRandomNonElectronicsProducts = async (limit = 10) => {
    const POOL_SIZE = 300;

    const { data, error } = await supabase
        .from('variants')
        .select('*')
        .not('product_type', 'ilike', 'electronics')
        .eq('is_hidden', false)
        .eq('is_available', true)
        .order('created_at', { ascending: false })
        .limit(POOL_SIZE);

    if (error) throw error;
    if (!data || data.length === 0) return [];

    // Group pool by shop
    const byShop = new Map();
    data.forEach((v) => {
        if (!v.shop_id) return;
        if (!byShop.has(v.shop_id)) byShop.set(v.shop_id, []);
        byShop.get(v.shop_id).push(v);
    });

    // Shuffle each shop's own items, and shuffle the shop order itself
    const shopIds = shuffleArray(Array.from(byShop.keys()));
    shopIds.forEach((id) => byShop.set(id, shuffleArray(byShop.get(id))));

    const result = [];
    let stillHasStock = true;
    while (result.length < limit && stillHasStock) {
        stillHasStock = false;
        for (const shopId of shopIds) {
            if (result.length >= limit) break;
            const pool = byShop.get(shopId);
            if (pool && pool.length > 0) {
                result.push(pool.shift());
                stillHasStock = stillHasStock || pool.length > 0;
            }
        }
    }

    return shuffleArray(result);
};